/**
 * Object Type Store - Mapping actions (entity-level + layer-level)
 */

import { generateId, getCurrentTimestamp, storage } from '../../lib/utils';
import { log, LogCategory } from '../../lib/logger';
import { getStorageKey } from './persistence';
import type { ParsedEntity } from '../../../map-editor-features/csv/lib/csvParser';
import type { Mapping, ObjectTypeState } from './types';

type Get = () => ObjectTypeState;
type Set = (partial: Partial<ObjectTypeState>) => void;

export const createMappingActions = (set: Set, get: Get) => ({
  addMapping: (mapping: Omit<Mapping, 'id' | 'created'>) => {
    const state = get();

    // Validate that the type exists
    const type = state.types.find((t) => t.id === mapping.typeId);
    if (!type) {
      throw new Error(`Object type with id "${mapping.typeId}" not found`);
    }

    // CRITICAL: Check for duplicate entityHandle + floorId combination
    // Use String() for type-safe comparison
    const existingMapping = state.mappings.find(
      (m) => m.entityHandle === mapping.entityHandle && String(m.floorId) === String(mapping.floorId)
    );

    if (existingMapping) {
      // Duplicate detected - log warning and skip
      log.warn(LogCategory.GENERAL, 'Duplicate mapping detected - skipping', {
        entityHandle: mapping.entityHandle,
        floorId: mapping.floorId,
        existingTypeId: existingMapping.typeId,
        newTypeId: mapping.typeId,
      });
      return;
    }

    const now = getCurrentTimestamp();
    const newMapping: Mapping = {
      id: generateId(),
      ...mapping,
      created: now,
    };

    const newMappings = [...state.mappings, newMapping];
    const newState = {
      types: state.types,
      mappings: newMappings,
    };

    // Persist to localStorage with current lot ID
    storage.set(getStorageKey(state.currentLotId), newState);

    set({ mappings: newMappings });
  },

  removeMapping: (id: string) => {
    const state = get();
    const newMappings = state.mappings.filter((m) => m.id !== id);
    const newState = {
      types: state.types,
      mappings: newMappings,
    };

    // Persist to localStorage with current lot ID
    storage.set(getStorageKey(state.currentLotId), newState);

    set({ mappings: newMappings });
  },

  getMappingsByFloorId: (floorId: string) => {
    // CRITICAL: Only return mappings for the specified floor
    // This prevents cross-floor contamination
    if (!floorId) {
      log.warn(LogCategory.GENERAL, 'getMappingsByFloorId called without floorId');
      return [];
    }
    // Use String() for type-safe comparison
    return get().mappings.filter((m) => String(m.floorId) === String(floorId));
  },

  // Get mapping for a specific entity handle on a specific floor
  getMappingForEntity: (entityHandle: string, floorId: string | null) => {
    if (!floorId || !entityHandle) return null;
    // Use String() for type-safe comparison
    return get().mappings.find(
      (m) => m.entityHandle === entityHandle && String(m.floorId) === String(floorId)
    ) || null;
  },

  removeMappingByEntity: (entityHandle: string, floorId?: string) => {
    const state = get();
    // CRITICAL: If floorId is provided, only remove mapping for that floor
    // This prevents removing mappings from other floors with the same entity handle
    // Use String() for type-safe comparison
    const mapping = floorId
      ? state.mappings.find((m) => m.entityHandle === entityHandle && String(m.floorId) === String(floorId))
      : state.mappings.find((m) => m.entityHandle === entityHandle);
    if (mapping) {
      get().removeMapping(mapping.id);
    }
  },

  removeMappingsByFloorId: (floorId: string) => {
    const state = get();
    // Use String() for type-safe comparison
    const removedCount = state.mappings.filter((m) => String(m.floorId) === String(floorId)).length;
    const newMappings = state.mappings.filter((m) => String(m.floorId) !== String(floorId));
    const newState = {
      types: state.types,
      mappings: newMappings,
    };

    log.info(LogCategory.GENERAL, '🗑️ Removed entity mappings by floorId', {
      floorId,
      removedCount,
      remainingCount: newMappings.length,
    });

    // Persist to localStorage with current lot ID
    storage.set(getStorageKey(state.currentLotId), newState);

    set({ mappings: newMappings });
  },

  getMappingByEntity: (floorId: string, entityHandle: string) => {
    // CRITICAL: Validate floorId to prevent cross-floor contamination
    if (!floorId || !entityHandle) {
      log.warn(LogCategory.GENERAL, 'getMappingByEntity called with invalid parameters', {
        floorId,
        entityHandle,
      });
      return undefined;
    }
    // Use String() for type-safe comparison
    return get().mappings.find(
      (m) => String(m.floorId) === String(floorId) && m.entityHandle === entityHandle
    );
  },

  // Layer Type Mapping actions

  getLayerTypeMappingsByFloorId: (floorId: string) => {
    // CRITICAL: Validate floorId to prevent cross-floor contamination
    if (!floorId) {
      log.warn(LogCategory.GENERAL, 'getLayerTypeMappingsByFloorId called without floorId');
      return [];
    }
    const layerTypeMappings = get().layerTypeMappings;
    if (!layerTypeMappings) {
      // Return empty array if layerTypeMappings is undefined
      return [];
    }
    // Use String() for type-safe comparison
    return layerTypeMappings.filter((m) => String(m.floorId) === String(floorId));
  },

  /**
   * 레이어 타입 매핑을 엔티티에 자동 적용
   * CSV 업로드 후 호출하여 이전에 저장된 매핑을 자동으로 적용
   */
  applyLayerTypeMappingsToEntities: (floorId: string, entities: ParsedEntity[]) => {
    const state = get();

    // CRITICAL: Check if layerTypeMappings exists
    if (!state.layerTypeMappings || !Array.isArray(state.layerTypeMappings)) {
      log.warn(LogCategory.GENERAL, 'layerTypeMappings is undefined or not an array - skipping', {
        floorId,
        layerTypeMappingsType: typeof state.layerTypeMappings,
      });
      return;
    }

    // Use String() for type-safe comparison
    const layerTypeMappings = state.layerTypeMappings.filter((m) => String(m.floorId) === String(floorId));

    log.info(LogCategory.GENERAL, '🔄 applyLayerTypeMappingsToEntities called', {
      floorId,
      entitiesCount: entities.length,
      layerTypeMappingsCount: layerTypeMappings.length,
      layerTypeMappingsPreview: layerTypeMappings.slice(0, 3).map(m => ({
        layerName: m.layerName,
        typeId: m.typeId
      }))
    });

    if (layerTypeMappings.length === 0) {
      log.info(LogCategory.GENERAL, 'No layer type mappings found for floor', { floorId });
      return;
    }

    // 레이어별 타입 맵 생성
    const layerToTypeMap = new Map<string, string>();
    layerTypeMappings.forEach((mapping) => {
      layerToTypeMap.set(mapping.layerName, mapping.typeId);
    });

    // 엔티티별 매핑 자동 생성
    const autoMappings: Omit<Mapping, 'id' | 'created'>[] = [];
    entities.forEach((entity) => {
      const typeId = layerToTypeMap.get(entity.layer);
      if (typeId) {
        // 이미 매핑이 있는지 확인
        // Use String() for type-safe comparison
        const existingMapping = state.mappings.find(
          (m) => String(m.floorId) === String(floorId) && m.entityHandle === entity.entityHandle
        );

        if (!existingMapping) {
          autoMappings.push({
            floorId,
            typeId,
            entityHandle: entity.entityHandle,
          });
        }
      }
    });

    // 자동 매핑 추가
    if (autoMappings.length > 0) {
      const newMappings: Mapping[] = autoMappings.map((m) => ({
        ...m,
        id: generateId(),
        created: getCurrentTimestamp(),
      }));

      const mappings = [...state.mappings, ...newMappings];
      set({ mappings });

      log.info(LogCategory.GENERAL, '✅ Auto-applied layer type mappings to entities', {
        floorId,
        autoMappedCount: newMappings.length,
        totalEntities: entities.length,
        newMappingsPreview: newMappings.slice(0, 3).map(m => ({
          entityHandle: m.entityHandle,
          typeId: m.typeId,
          floorId: m.floorId
        }))
      });
    } else {
      log.warn(LogCategory.GENERAL, '⚠️ No new mappings to apply (all entities already mapped)', {
        floorId,
        totalEntities: entities.length,
      });
    }
  },
});
